import Api, { isCancelError } from "../../core/Api";
import _ from "lodash";
import { createAsyncManager } from "../../Utils/AsyncManager";

export const REQUEST_DATA = "REQUEST_DATA";
export const POST_DATA = "POST_DATA";
export const NETWORK_ERROR = "NETWORK_ERROR";
export const CANCEL_REQUEST = "CANCEL_REQUEST";
export const RECEIVE_DATA = "RECEIVE_DATA";
export const REQUEST_ERROR = "REQUEST_ERROR";

const requestData = (source) => ({
  type: REQUEST_DATA,
  source,
});

export const postRequest = (source, data) => ({
  type: POST_DATA,
  source,
  data,
});

const networkError = (source, error) => ({
  type: NETWORK_ERROR,
  source,
  error,
  receivedAt: Date.now(),
});

const cancelRequest = (source) => ({
  type: CANCEL_REQUEST,
  source,
});

const receiveData = (source, data, status) => ({
  type: RECEIVE_DATA,
  source,
  data,
  status,
  receivedAt: Date.now(),
});

const requestError = (source, error, status) => ({
  type: REQUEST_ERROR,
  source,
  error,
  status,
  receivedAt: Date.now(),
});

const handleResponse = (source, request, receiver, errorReceiver, dispatch) =>
  request
    .then((response) => {
      dispatch(receiveData(source, response.data, response.status));
      return dispatch(receiver(response.data, response.status));
    })
    .catch((error) => {
      console.log(JSON.parse(JSON.stringify(error)));
      if (errorReceiver) {
        dispatch(errorReceiver(source, error));
        console.log("receive error");
      }
      if (error.response) {
        if (
          error.response.headers &&
          error.response.headers["content-type"] === "application/json" &&
          _.isObject(error.response.data)
        ) {
          // request error (generated by the server)
          // dispatch(receiveData(source, error.response.data, error.response.status));
          // return dispatch(receiver(error.response.data));
          Api.setToken("");
          return dispatch(requestError(source, error.response.data, error.response.status));
        }
      }
      if (_.isObject(error.response) && error.response.status >= 400 && error.response.status < 500) {
        return dispatch(requestError(source, error));
      }
      if (isCancelError(error)) {
        return dispatch(cancelRequest(source));
      }
      return dispatch(networkError(source, error));
    });

export const fetchData = (source, receiver, errorReceiver) => (dispatch) => {
  dispatch(requestData(source));
  return handleResponse(source, Api.request(source), receiver, errorReceiver, dispatch);
};

const defaultRequestManager = createAsyncManager((...args) => Api.request(...args), {
  getKey: (sourceConfig) => {
    if (typeof sourceConfig === "string") {
      return sourceConfig;
    } else if (typeof sourceConfig === "object" && sourceConfig.url) {
      return sourceConfig.url;
    } else {
      throw new TypeError("The sourceConfig has to be a string or an object with a url property!");
    }
  },
});

export const asyncRequest = (source, receiver, extraData = {}, options = {}, errorReceiver) => async (
  dispatch
) => {
  const { requestManager = defaultRequestManager, useLocalErrorHandling = false } = options;
  const request = requestManager.create(source);
  try {
    const response = await request.call();
    dispatch(receiveData(source, response.data));
    return receiver ? dispatch(receiver(response.data, extraData, response)) : response;
  } catch (error) {
    if (request.isOutdated()) {
      return;
    }
    if (useLocalErrorHandling) {
      throw error;
    }
    if (errorReceiver) {
      dispatch(errorReceiver(source, error));
      console.log("receive error");
    }
    if (_.isObject(error.response)) {
      if (error.response.status === 404) {
        dispatch(requestError(source, error));
      }
      if (_.isObject(error.response.data)) {
        // request error (generated by the server)
        dispatch(receiveData(source, error));
        return receiver ? dispatch(receiver(error.response.data, extraData, error.response)) : error.response;
      }
      if (error.response.status >= 400 && error.response.status < 500) {
        dispatch(requestError(source, error));
      }
    }
    if (isCancelError(error)) {
      dispatch(cancelRequest(source));
    } else {
      dispatch(networkError(source, error));
    }
    throw error;
  }
};

export const fetchDataAsync = (source, receiver, extraData = {}) => (dispatch) => {
  dispatch(requestData(source));
  return dispatch(asyncRequest(source, receiver, extraData));
};

export const postDataAsync = (
  source,
  data,
  receiver,
  errorReceiver,
  config = {},
  extraData = {},
  options = {}
) => (dispatch) => {
  dispatch(postRequest(source, data));
  return dispatch(
    asyncRequest(
      { url: source, method: "post", data: data, ...config },
      receiver,
      extraData,
      options,
      errorReceiver
    )
  );
};

export const postData = (source, data, receiver, errorReceiver) => (dispatch) => {
  dispatch(postRequest(source, data));
  return handleResponse(
    source,
    Api.request({ url: source, method: "post", data }),
    receiver,
    errorReceiver,
    dispatch
  );
};

export const deleteData = (source, data, receiver, errorReceiver) => (dispatch) => {
  dispatch(postRequest(source, data));
  return handleResponse(
    source,
    Api.request({ url: source, method: "delete", data }),
    receiver,
    errorReceiver,
    dispatch
  );
};
